import React from 'react';
import pt from 'prop-types';
import { connect } from 'react-redux';
import ViewLoading from 'components/view/loading';
import StorefrontNewPage from 'components/storefront/new';
import StorefrontNewCustomPage from 'components/storefront/new-custom';
import StorefrontCreatedPage from 'components/storefront/created';
import { getAppSlugId } from 'utils/app';
import actions from 'actions';
import api from 'services/api';

export const mapStateToProps = (state) => ({
  client: state.client,
  apps: state.apps.storefrontShops,
  installedApps: state.client.apps,
  storefronts: state.storefronts.all,
  primaryStorefront: state.storefronts.primary,
  loading: state.storefronts.loading,
  loadingApps: state.apps.loading,
});

export const mapDispatchToProps = (dispatch) => ({
  fetchApp: (id) => {
    return dispatch(actions.apps.fetch(id));
  },

  fetchAppWithPreviewTheme: (id) => {
    return dispatch(actions.apps.fetchWithPreviewTheme(id));
  },

  fetchInstalledApps: () => {
    return dispatch(actions.client.fetchInstalledApps());
  },

  fetchAllStorefrontShopApps: (catalogFilters) => {
    return dispatch(
      actions.apps.fetchAllStorefrontShops({ filters: catalogFilters }),
    );
  },

  autoUpdateInstalledThemes: () => {
    return dispatch(actions.apps.autoUpdateInstalledThemes());
  },

  installApp: (id) => {
    return dispatch(actions.client.installApp(id));
  },

  fetchStorefront: (id) => {
    return dispatch(actions.storefronts.fetch(id));
  },

  fetchAllStorefronts: () => {
    return dispatch(actions.storefronts.fetchAll());
  },

  loadAllStorefronts: () => {
    return dispatch(actions.storefronts.loadAll());
  },

  createStorefront: (data) => {
    return dispatch(actions.storefronts.create(data));
  },

  createStorefrontWithApp: (app, data) => {
    return dispatch(actions.storefronts.createWithApp(app, data));
  },

  uninstallApp: (id) => {
    return dispatch(actions.client.uninstallApp(id));
  },

  deleteApp: (id) => {
    return dispatch(actions.client.deleteApp(id));
  },
});

export class NewStorefront extends React.Component {
  static contextTypes = {
    client: pt.object.isRequired,
    openModal: pt.func.isRequired,
    notifySuccess: pt.func.isRequired,
    notifyError: pt.func.isRequired,
  };

  static propTypes = {
    params: pt.object,
    router: pt.object,
    client: pt.object,
    storefronts: pt.object,
    installedApps: pt.object,
    apps: pt.object,

    fetchApp: pt.func,
    fetchStorefront: pt.func,
    fetchInstalledApps: pt.func,
    fetchAppWithPreviewTheme: pt.func,
    fetchAllStorefrontShopApps: pt.func,
    autoUpdateInstalledThemes: pt.func,
    loadAllStorefronts: pt.func,
    createStorefront: pt.func,
    createStorefrontWithApp: pt.func,
    createThemeApp: pt.func,
    uninstallApp: pt.func,
    installApp: pt.func,
    deleteApp: pt.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      storefrontCreated: null,
      submittedFindAPartner: false,
      onCreateStorefront: this.onCreateStorefront.bind(this),
      onCreateStorefrontWithApp: this.onCreateStorefrontWithApp.bind(this),
      onUploadTheme: this.onUploadTheme.bind(this),
      onUninstallDeleteApp: this.onUninstallDeleteApp.bind(this),
      onSubmitFindAPartner: this.onSubmitFindAPartner.bind(this),
      fetchAppList: this.fetchAppList.bind(this),
    };
  }

  async componentDidMount() {
    const {
      params,
      fetchStorefront,
      fetchApp,
      fetchAppWithPreviewTheme,
      autoUpdateInstalledThemes,
      loadAllStorefronts,
    } = this.props;

    await autoUpdateInstalledThemes();

    if (params.storefront_id) {
      const storefront = await fetchStorefront(params.storefront_id);
      if (storefront) {
        const app = storefront.theme_app_id
          ? await fetchAppWithPreviewTheme(storefront.theme_app_id)
          : await fetchApp(storefront.app_id);
        this.setState({ storefrontCreated: { storefront, app } });
      }
    } else {
      await loadAllStorefronts();
    }

    this.setState({ loaded: true });
  }

  fetchAppList(catalogFilters) {
    const { fetchAllStorefrontShopApps } = this.props;

    return fetchAllStorefrontShopApps(catalogFilters);
  }

  fetchLibraryApps() {
    const { fetchInstalledApps, loadAllStorefronts } = this.props;

    return Promise.all([
      this.fetchAppList(),
      fetchInstalledApps(),
      loadAllStorefronts(),
    ]);
  }

  async onCreateStorefront(values) {
    const { router, createStorefront } = this.props;

    const result = await createStorefront(values);

    if (!result) {
      this.context.notifyError('Failed to create storefront.');
      return;
    }

    if (result.errors) {
      this.context.notifyError(result.errors);
      return;
    }

    this.context.notifySuccess('Storefront created');
    router.replace('/storefronts');
  }

  async onCreateStorefrontWithApp(app, values) {
    const { createStorefrontWithApp } = this.props;

    const result = await createStorefrontWithApp(app, values);

    if (!result) {
      this.context.notifyError('Failed to create storefront with app.');
      return;
    }

    if (result.errors) {
      this.context.notifyError(result.errors);
      return;
    }

    this.setState({ storefrontCreated: { storefront: result, app } });
    return result;
  }

  async onUploadTheme(result) {
    if (result) {
      await this.fetchLibraryApps();

      if (result.upload_create_storefront) {
        const { fetchAllStorefronts } = this.props;

        const storefronts = await fetchAllStorefronts();
        const storefront = storefronts.results.find(
          (storefront) => storefront.theme_app_id === result.id,
        );

        this.setState({ storefrontCreated: { storefront, app: result } });
      }
    }
  }

  onUninstallDeleteApp(app) {
    const { uninstallApp, deleteApp, storefronts } = this.props;

    const appStorefronts = storefronts.results?.filter((storefront) =>
      app.type === 'theme'
        ? storefront.theme_app_id === app.id
        : storefront.app_id === app.id,
    );

    this.context.openModal('UninstallDeleteApp', {
      app,
      appStorefronts,
      uninstallApp,
      deleteApp,
      onSuccess: () => {
        return this.fetchLibraryApps();
      },
    });
  }

  async onSubmitFindAPartner(values) {
    this.setState({ submittedFindAPartner: false });
    try {
      await api.post('/find-a-partner', { request: values });
    } catch (err) {
      console.error(err);
    }
    this.setState({ submittedFindAPartner: true });
  }

  render() {
    const { params, router } = this.props;
    const { storefrontCreated } = this.state;

    if (!this.state.loaded) {
      return <ViewLoading />;
    }

    if (storefrontCreated) {
      return <StorefrontCreatedPage {...this.props} {...storefrontCreated} />;
    } else if (params.type === 'custom') {
      return <StorefrontNewCustomPage {...this.props} {...this.state} />;
    } else if (params.type) {
      router.replace('/storefronts');
      return null;
    }

    return <StorefrontNewPage {...this.props} {...this.state} />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(NewStorefront);
